import { createContext, useContext, useState, useEffect } from "react";

// ── Bengali translations — UI labels only ────────────────────────────────────
// Medical values, patient data, and report content stay in English.
export const translations = {
  en: {
    // Navigation
    dashboard:    "Dashboard",
    patients:     "Patients",
    labReports:   "Lab Reports",
    imaging:      "Imaging",
    settings:     "Settings",
    system:       "System",
    signOut:      "Sign Out",

    // Dashboard
    welcome:         "Welcome",
    totalPatients:   "Total patients",
    totalReports:    "Total reports",
    testCategories:  "Test categories",
    recentLabReports:"Recent lab reports",
    viewAll:         "View all →",
    byReportType:    "By report type",
    quickAccess:     "Quick access",
    allPatients:     "All patients",
    allUploaded:     "All uploaded",
    reportTypes:     "Report types",
    newThisMonth:    (n) => `+${n} this month`,
    uploadReport:    "Upload report",
    registerPatient: "Register patient",

    // Patients
    registeredPatients: (n) => `${n} registered patients`,
    searchPlaceholder:  "Search by name, EMR ID, phone, doctor — or #108 for Lab ID...",
    noPatients:         "No patients registered",
    noResults:          "No patients found",
    tryDifferent:       "Try a different search term.",
    getStarted:         "Register your first patient to get started.",
    deletedPatients:    "Deleted patients",
    registerNew:        "Register Patient",
    template:           "⬇ Template",
    importOne:          "⬆ Import",
    bulkImport:         "⬆ Bulk Import",
    restore:            "Restore",
    restoring:          "Restoring...",
    deleteAllPerm:      "Delete all permanently",
    noDeletedPatients:  "No deleted patients.",

    // Patient form
    registerNewPatient: "Register New Patient",
    step:               (s, t) => `Step ${s} of ${t}`,
    personalInfo:       "Personal Information",
    medicalInfo:        "Medical Information",
    fullName:           "Full Name",
    age:                "Age",
    sex:                "Sex",
    bloodGroup:         "Blood Group",
    phoneNumber:        "Phone Number",
    address:            "Address",
    referredDoctor:     "Referred Doctor",
    emergencyContact:   "Emergency Contact",
    notes:              "Notes / Known Conditions",
    preview:            "Preview",
    next:               "Next →",
    back:               "← Back",
    cancel:             "Cancel",
    registering:        "Registering...",
    registerPatientBtn: "Register Patient",
    duplicateFound:     "Possible duplicate patient found",
    registerAnyway:     "Register as new patient anyway",
    openExisting:       "Open existing patient →",

    // Settings
    settingsTitle:      "Settings",
    securityConfig:     "Security and system configuration",
    passwords:          "Passwords",
    masterCode:         "Master Code",
    pinLock:            "PIN Lock",
    auditLog:           "Audit Log",
    twoFactorAuth:      "Two-Factor Auth",

    // Imaging
    imagingRegister:    "Imaging Register",
    imagingSubtitle:    "X-Ray, CT, MRI, Ultrasound and all imaging studies",
    uploadImaging:      "+ Upload Imaging",
    patientsWithImaging:"Patients with Imaging",
    totalStudies:       "Total Studies",
    today:              "Today",
    modalities:         "Modalities",

    // Topbar date
    dateLocale:  "en-GB",

    // Common
    loading:     "Loading...",
    save:        "Save",
    saving:      "Saving...",
    delete:      "Delete",
    edit:        "Edit",
    close:       "Close",
    confirm:     "Confirm",
    yes:         "Yes",
    no:          "No",
    search:      "Search",
    filter:      "Filter",
    refresh:     "Refresh",
    enabled:     "Enabled",
    disabled:    "Disabled",
    active:      "Active",
    unknown:     "Unknown",
    workQueue:    "Work Queue",

    // Lab Reports
    labRegister:       "Lab Register",
    allLabReports:     "All laboratory reports across all patients",
    totalReportsLabel: "Total Reports",
    abnormal:          "Abnormal",
    normal:            "Normal",
    testTypes:         "Test Types",
    searchReports:     "Search by patient name, EMR ID, lab ID, test type...",
    dateLabel:         "Date:",
    typeLabel:         "Type:",
    statusLabel:       "Status:",
    allOption:         "All",
    thisWeek:          "This Week",
    thisMonth:         "This Month",
    thisYear:          "This Year",
    labIdCol:          "Lab ID",
    patientCol:        "Patient",
    typeCol:           "Type",
    dateCol:           "Date",
    labCol:            "Lab",
    resultCol:         "Result",
    actionCol:         "Action",
    viewAction:        "View →",

    // Imaging
    imagingSubtitleBn: "X-Ray, CT, MRI, Ultrasound and all imaging studies",
    uploadImagingBtn:  "+ Upload Imaging",
    patientsWithImg:   "Patients with Imaging",
    patientCol2:       "Patient",
    modalitiesCol:     "Modalities",
    lastStudy:         "Last Study",
    studiesCol:        "Studies",

    // Settings
    settingsSec:       "Security and system configuration",
    passwordsTab:      "Passwords",
    twoFactorTab:      "Two-Factor Auth",
    pinLockTab:        "PIN Lock",
    auditLogTab:       "Audit Log",
    systemTab:         "System",
    changePassword:    "Change Password",
    selectRole:        "Select Role",
    newPassword:       "New Password",
    confirmPassword:   "Confirm Password",
    updatePassword:    "Update Password",
    adminRecoveryEmail:"Admin Recovery Email",
    recoveryEmail:     "Recovery Email",
    saveEmail:         "Save Email",
    forPasswordReset:  "For password reset",

    // System
    systemTitle:       "System Status",
    allSystemsOk:      "All systems operational",
    systemIssue:       "System issue detected",
    systemInfo:        "System info",
    restoreSection:    "Restore",
    backupSection:     "Backup",
    existingSkippedNote: "⚠ Existing records will be skipped.",
    fromR2Btn:         "☁ From R2",
    fromPCBtn:         "💻 From PC",
    version:           "Version",
    backend:           "Backend",
    database:          "Database",
    storage:           "Storage",
    monitorUrl:        "Monitor URL",
    backup:            "Backup",
    streamingBackup:   "Streaming backup — zero disk usage. Streams R2 → zip → backup R2.",
    createBackup:      "⬇ Create Backup",
    downloadLatest:    "⬇ Download Latest",
    onRailwayDisk:     "On Railway disk",
    existingSkipped:   "⚠ Existing records will be skipped.",
    fromR2:            "☁ From R2",
    fromPC:            "💻 From PC",
    restoreFromR2:     "▶ Restore from R2",
    storageCleanup:    "Storage Cleanup",
    scanOrphaned:      "🔍 Scan for Orphaned Files",
    maintenanceMode:   "Maintenance Mode",
    creatingBackup:    "Creating...",
    downloading:       "Downloading...",
    restoring2:        "Restoring...",
    scanning:          "Scanning...",

    // Patient Detail
    backToPatients:    "Back to Patients",
    exportExcel:       "Export Excel",
    allCategories:     "All Categories",
    reports:           "reports",
    latest:            "LATEST",
    edited:            "Edited",
    pinBtn:            "Pin",
    viewBtn:           "View →",
    editValues:        "Edit Values",
    trend:             "Trend →",
    deleteBtn:         "Delete",
    saveBtn:           "Save",
    cancelBtn:         "Cancel",
    valueIndicators:   "Value Indicators",
    parameter:         "Parameter",
    result:            "Result",
    unit:              "Unit",
    reference:         "Reference",
    status2:           "Status",
    outsideRange:      (n) => `${n} value(s) outside normal range`,
    noReports:         "No test reports available for this patient.",
    uploadToAdd:       "Use the Upload Report button to add test results.",
    trendAnalysis:     "Trend Analysis",
    custom:            "Custom",
    from:              "From",
    to:                "To",
    onlyOneReport:     "Only 1 report in this range — expand date range to see the trend chart",

    // Sidebar / Logo
    instituteName:     "Nashman Institute",
    instituteSubtitle: "Pathology & Regenerative Medicine",
    administrator:     "Administrator",
    doctor2:           "Doctor",
    technician2:       "Technician",
    receptionist2:     "Receptionist",
  },

  bn: {
    // Navigation
    dashboard:    "ড্যাশবোর্ড",
    patients:     "রোগী",
    labReports:   "ল্যাব রিপোর্ট",
    imaging:      "ইমেজিং",
    settings:     "সেটিংস",
    system:       "সিস্টেম",
    signOut:      "সাইন আউট",

    // Dashboard
    welcome:         "স্বাগতম",
    totalPatients:   "মোট রোগী",
    totalReports:    "মোট রিপোর্ট",
    testCategories:  "পরীক্ষার ধরন",
    recentLabReports:"সাম্প্রতিক ল্যাব রিপোর্ট",
    viewAll:         "সব দেখুন →",
    byReportType:    "রিপোর্টের ধরন অনুযায়ী",
    quickAccess:     "দ্রুত প্রবেশ",
    allPatients:     "সকল রোগী",
    allUploaded:     "সব আপলোড হয়েছে",
    reportTypes:     "রিপোর্টের ধরন",
    newThisMonth:    (n) => `+${n} এই মাসে`,
    uploadReport:    "রিপোর্ট আপলোড",
    registerPatient: "রোগী নিবন্ধন",

    // Patients
    registeredPatients: (n) => `${n} জন নিবন্ধিত রোগী`,
    searchPlaceholder:  "নাম, EMR ID, ফোন, ডাক্তার দিয়ে খুঁজুন — অথবা #108 Lab ID এর জন্য...",
    noPatients:         "কোনো রোগী নিবন্ধিত নেই",
    noResults:          "কোনো রোগী পাওয়া যায়নি",
    tryDifferent:       "অন্য কিছু দিয়ে খোঁজার চেষ্টা করুন।",
    getStarted:         "শুরু করতে প্রথম রোগী নিবন্ধন করুন।",
    deletedPatients:    "মুছে ফেলা রোগী",
    registerNew:        "রোগী নিবন্ধন",
    template:           "⬇ টেমপ্লেট",
    importOne:          "⬆ ইম্পোর্ট",
    bulkImport:         "⬆ বাল্ক ইম্পোর্ট",
    restore:            "পুনরুদ্ধার",
    restoring:          "পুনরুদ্ধার হচ্ছে...",
    deleteAllPerm:      "সব স্থায়ীভাবে মুছুন",
    noDeletedPatients:  "কোনো মুছে ফেলা রোগী নেই।",

    // Patient form
    registerNewPatient: "নতুন রোগী নিবন্ধন",
    step:               (s, t) => `ধাপ ${s} এর ${t}`,
    personalInfo:       "ব্যক্তিগত তথ্য",
    medicalInfo:        "চিকিৎসা তথ্য",
    fullName:           "পূর্ণ নাম",
    age:                "বয়স",
    sex:                "লিঙ্গ",
    bloodGroup:         "রক্তের গ্রুপ",
    phoneNumber:        "ফোন নম্বর",
    address:            "ঠিকানা",
    referredDoctor:     "প্রেরণকারী ডাক্তার",
    emergencyContact:   "জরুরি যোগাযোগ",
    notes:              "নোট / পরিচিত রোগ",
    preview:            "পূর্বরূপ",
    next:               "পরবর্তী →",
    back:               "← ফিরে যান",
    cancel:             "বাতিল",
    registering:        "নিবন্ধন হচ্ছে...",
    registerPatientBtn: "রোগী নিবন্ধন করুন",
    duplicateFound:     "সম্ভাব্য ডুপ্লিকেট রোগী পাওয়া গেছে",
    registerAnyway:     "তবুও নতুন রোগী হিসেবে নিবন্ধন করুন",
    openExisting:       "বিদ্যমান রোগী খুলুন →",

    // Settings
    settingsTitle:      "সেটিংস",
    securityConfig:     "নিরাপত্তা ও সিস্টেম কনফিগারেশন",
    passwords:          "পাসওয়ার্ড",
    masterCode:         "মাস্টার কোড",
    pinLock:            "PIN লক",
    auditLog:           "অডিট লগ",
    twoFactorAuth:      "দুই-স্তরীয় যাচাই",

    // Imaging
    imagingRegister:    "ইমেজিং রেজিস্টার",
    imagingSubtitle:    "এক্স-রে, CT, MRI, আলট্রাসাউন্ড এবং সকল ইমেজিং",
    uploadImaging:      "+ ইমেজিং আপলোড",
    patientsWithImaging:"ইমেজিং সহ রোগী",
    totalStudies:       "মোট স্টাডি",
    today:              "আজকে",
    modalities:         "মোডালিটি",

    // Topbar date
    dateLocale:  "bn-BD",

    // Common
    loading:     "লোড হচ্ছে...",
    save:        "সংরক্ষণ",
    saving:      "সংরক্ষণ হচ্ছে...",
    delete:      "মুছুন",
    edit:        "সম্পাদনা",
    close:       "বন্ধ করুন",
    confirm:     "নিশ্চিত করুন",
    yes:         "হ্যাঁ",
    no:          "না",
    search:      "খুঁজুন",
    filter:      "ফিল্টার",
    refresh:     "রিফ্রেশ",
    enabled:     "সক্রিয়",
    disabled:    "নিষ্ক্রিয়",
    active:      "সক্রিয়",
    unknown:     "অজানা",
    workQueue:    "ওয়ার্ক কিউ",

    // Lab Reports
    labRegister:       "ল্যাব রেজিস্টার",
    allLabReports:     "সকল রোগীর ল্যাব রিপোর্ট",
    totalReportsLabel: "মোট রিপোর্ট",
    abnormal:          "অস্বাভাবিক",
    normal:            "স্বাভাবিক",
    testTypes:         "পরীক্ষার ধরন",
    searchReports:     "রোগীর নাম, EMR ID, Lab ID, পরীক্ষার ধরন দিয়ে খুঁজুন...",
    dateLabel:         "তারিখ:",
    typeLabel:         "ধরন:",
    statusLabel:       "অবস্থা:",
    allOption:         "সব",
    thisWeek:          "এই সপ্তাহ",
    thisMonth:         "এই মাস",
    thisYear:          "এই বছর",
    labIdCol:          "ল্যাব ID",
    patientCol:        "রোগী",
    typeCol:           "ধরন",
    dateCol:           "তারিখ",
    labCol:            "ল্যাব",
    resultCol:         "ফলাফল",
    actionCol:         "কার্যক্রম",
    viewAction:        "দেখুন →",

    // Imaging
    imagingSubtitleBn: "এক্স-রে, CT, MRI, আলট্রাসাউন্ড এবং সকল ইমেজিং স্টাডি",
    uploadImagingBtn:  "+ ইমেজিং আপলোড",
    patientsWithImg:   "ইমেজিং সহ রোগী",
    patientCol2:       "রোগী",
    modalitiesCol:     "মোডালিটি",
    lastStudy:         "সর্বশেষ স্টাডি",
    studiesCol:        "স্টাডি",

    // Settings
    settingsSec:       "নিরাপত্তা ও সিস্টেম কনফিগারেশন",
    passwordsTab:      "পাসওয়ার্ড",
    twoFactorTab:      "দুই-স্তরীয় যাচাই",
    pinLockTab:        "PIN লক",
    auditLogTab:       "অডিট লগ",
    systemTab:         "সিস্টেম",
    changePassword:    "পাসওয়ার্ড পরিবর্তন",
    selectRole:        "ভূমিকা নির্বাচন",
    newPassword:       "নতুন পাসওয়ার্ড",
    confirmPassword:   "পাসওয়ার্ড নিশ্চিত করুন",
    updatePassword:    "পাসওয়ার্ড আপডেট করুন",
    adminRecoveryEmail:"অ্যাডমিন রিকভারি ইমেইল",
    recoveryEmail:     "রিকভারি ইমেইল",
    saveEmail:         "ইমেইল সংরক্ষণ",
    forPasswordReset:  "পাসওয়ার্ড রিসেটের জন্য",

    // System
    systemTitle:       "সিস্টেম স্ট্যাটাস",
    allSystemsOk:      "সব সিস্টেম স্বাভাবিক",
    systemIssue:       "সিস্টেমে সমস্যা আছে",
    systemInfo:        "সিস্টেম তথ্য",
    restoreSection:    "রিস্টোর",
    backupSection:     "ব্যাকআপ",
    existingSkippedNote: "⚠ বিদ্যমান রেকর্ড এড়িয়ে যাওয়া হবে।",
    fromR2Btn:         "☁ R2 থেকে",
    fromPCBtn:         "💻 PC থেকে",
    version:           "ভার্সন",
    backend:           "ব্যাকএন্ড",
    database:          "ডেটাবেস",
    storage:           "স্টোরেজ",
    monitorUrl:        "মনিটর URL",
    backup:            "ব্যাকআপ",
    streamingBackup:   "স্ট্রিমিং ব্যাকআপ — ডিস্ক ব্যবহার শূন্য।",
    createBackup:      "⬇ ব্যাকআপ তৈরি করুন",
    downloadLatest:    "⬇ সর্বশেষ ডাউনলোড",
    onRailwayDisk:     "Railway ডিস্কে",
    existingSkipped:   "⚠ বিদ্যমান রেকর্ড এড়িয়ে যাওয়া হবে।",
    fromR2:            "☁ R2 থেকে",
    fromPC:            "💻 PC থেকে",
    restoreFromR2:     "▶ R2 থেকে রিস্টোর",
    storageCleanup:    "স্টোরেজ পরিষ্কার",
    scanOrphaned:      "🔍 অনাথ ফাইল খুঁজুন",
    maintenanceMode:   "রক্ষণাবেক্ষণ মোড",
    creatingBackup:    "তৈরি হচ্ছে...",
    downloading:       "ডাউনলোড হচ্ছে...",
    restoring2:        "রিস্টোর হচ্ছে...",
    scanning:          "স্ক্যান হচ্ছে...",

    // Patient Detail
    backToPatients:    "রোগীদের তালিকায় ফিরুন",
    exportExcel:       "এক্সেল এক্সপোর্ট",
    allCategories:     "সব বিভাগ",
    reports:           "রিপোর্ট",
    latest:            "সর্বশেষ",
    edited:            "সম্পাদিত",
    pinBtn:            "পিন",
    viewBtn:           "দেখুন →",
    editValues:        "মান সম্পাদনা",
    trend:             "ট্রেন্ড →",
    deleteBtn:         "মুছুন",
    saveBtn:           "সংরক্ষণ",
    cancelBtn:         "বাতিল",
    valueIndicators:   "মান নির্দেশক",
    parameter:         "প্যারামিটার",
    result:            "ফলাফল",
    unit:              "একক",
    reference:         "রেফারেন্স",
    status2:           "অবস্থা",
    outsideRange:      (n) => `${n}টি মান স্বাভাবিক সীমার বাইরে`,
    noReports:         "এই রোগীর কোনো পরীক্ষার রিপোর্ট নেই।",
    uploadToAdd:       "ফলাফল যোগ করতে রিপোর্ট আপলোড বাটন ব্যবহার করুন।",
    trendAnalysis:     "ট্রেন্ড বিশ্লেষণ",
    custom:            "কাস্টম",
    from:              "থেকে",
    to:                "পর্যন্ত",
    onlyOneReport:     "এই সীমায় মাত্র ১টি রিপোর্ট — ট্রেন্ড চার্ট দেখতে তারিখ প্রসারিত করুন",

    // Sidebar / Logo
    instituteName:     "নাশমান ইন্সটিটিউট",
    instituteSubtitle: "প্যাথলজি ও রিজেনারেটিভ মেডিসিন",
    administrator:     "প্রশাসক",
    doctor2:           "ডাক্তার",
    technician2:       "টেকনিশিয়ান",
    receptionist2:     "রিসেপশনিস্ট",
  },
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [username, setUsername] = useState("default");
  const langKey = `emr_language_${username}`;

  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("emr_language_default") || "en";
  });

  const t = translations[language] || translations.en;

  const toggleLanguage = () => {
    setLanguage(prev => {
      const next = prev === "en" ? "bn" : "en";
      localStorage.setItem(langKey, next);
      return next;
    });
  };

  const setActiveUsername = (name) => {
    const uname = name || "default";
    setUsername(uname);
    const saved = localStorage.getItem(`emr_language_${uname}`) || "en";
    setLanguage(saved);
  };

  // Remove any leftover dark class on mount (in case it was previously enabled)
  useEffect(() => {
    document.documentElement.classList.remove("dark");
    localStorage.removeItem("emr_dark_mode");
  }, []);

  return (
    <ThemeContext.Provider value={{ language, toggleLanguage, t, setActiveUsername }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}

// Convert English digits to Bengali numerals when Bengali is active
const BN_DIGITS = ["০","১","২","৩","৪","৫","৬","৭","৮","৯"];
export function toBengaliNum(n, language) {
  if (language !== "bn") return String(n);
  return String(n).replace(/[0-9]/g, d => BN_DIGITS[parseInt(d)]);
}